import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';
import MeetingStatus from 'enums/MeetingStatus';
import { select } from 'redux-saga/effects';
import { call, put } from 'store/utils';
import * as api from 'utils/apiCalls';
import * as chimeActions from './chimeActions';

export function* hasMeetingBeenCreatedSaga() {
  yield put(chimeActions.initAction({ key: 'hasMeetingBeenCreated' }));
  try {
    const { externalMeetingId, push } = yield select(({ chime, utils }) => ({
      externalMeetingId: chime.externalMeetingId,
      push: utils.push,
    }));
    const meetingCreated = yield call(api.hasMeetingBeenCreated, externalMeetingId);
    yield put(chimeActions.setHasMeetingBeenCreatedAction(meetingCreated));
    yield put(chimeActions.successAction({ key: 'hasMeetingBeenCreated' }));
    yield call(push('/'));
  } catch (error) {
    yield put(chimeActions.setMeetingStatusAction({ meetingStatus: MeetingStatus.Failed }));
    yield put(chimeActions.errorAction({ key: 'hasMeetingBeenCreated', error: error.message }));
  }
}

export function* getJoinInfoSaga() {
  try {
    yield put(chimeActions.initAction({ key: 'getJoinInfo' }));
    const { externalMeetingId, externalUserId } = yield select(({ chime }) => ({
      externalMeetingId: chime.externalMeetingId,
      externalUserId: chime.externalUserId,
    }));
    const response = yield api.joinMeeting(externalMeetingId, externalUserId);
    if (response.error) throw response.error;
    const { Meeting, Attendee, hostId, ExternalMeetingId, Name } = response?.JoinInfo;

    const configuration = new MeetingSessionConfiguration(Meeting, Attendee);
    const logger = new ConsoleLogger('SDK', LogLevel.OFF);
    const deviceController = new DefaultDeviceController(logger);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

    const init = {
      title: ExternalMeetingId || externalMeetingId,
      externalMeetingId: ExternalMeetingId || externalMeetingId,
      externalUserId: configuration?.credentials?.externalUserId || externalUserId,
      attendeeId: Attendee.AttendeeId,
      hostId,
      meetingId: configuration?.meetingId,
      name: Name,
      isHost: hostId === externalUserId,
      audioVideo: meetingSession.audioVideo,
      meetingSession,
      configuration,
    };

    yield put(chimeActions.setJoinInfoAction(init));
    yield put(chimeActions.successAction({ key: 'getJoinInfo' }));
    return init;
  } catch (error) {
    yield put(chimeActions.errorAction({ key: 'getJoinInfo', error: error.message }));
  }
}

export function* getAudioVideoDevicesSaga() {
  yield put(chimeActions.initAction({ key: 'getAudioVideoDevices' }));
  try {
    const { audioVideo, isRecordingUser } = yield select(({ chime, user }) => ({
      audioVideo: chime.audioVideo,
      isRecordingUser: user.isRecordingUser,
    }));

    const audioInputDevices = (yield audioVideo.listAudioInputDevices()).map(
      ({ label, deviceId }) => ({
        label,
        value: deviceId,
      })
    );

    const audioOutputDevices = (yield audioVideo.listAudioOutputDevices()).map(
      ({ label, deviceId }) => ({
        label,
        value: deviceId,
      })
    );

    const videoInputDevices = (yield audioVideo.listVideoInputDevices()).map(
      ({ label, deviceId }) => ({
        label,
        value: deviceId,
      })
    );

    yield put(
      chimeActions.setAudioDeviceListsAction({
        audioInputDevices,
        audioOutputDevices,
        videoInputDevices,
      })
    );
    if (isRecordingUser) {
      audioVideo.realtimeMuteLocalAudio();
      audioVideo.realtimeSetCanUnmuteLocalAudio(false);
    } else {
      if (audioInputDevices.length > 0) {
        yield audioVideo.chooseAudioInputDevice(audioInputDevices?.[0]?.value);
        yield put(
          chimeActions.setCurrentDeviceAction({
            deviceType: 'currentAudioInputDevice',
            deviceValue: audioInputDevices?.[0]?.value,
          })
        );
      }
      if (audioOutputDevices.length > 0) {
        yield audioVideo.chooseAudioOutputDevice(audioOutputDevices?.[0]?.value);
        yield put(
          chimeActions.setCurrentDeviceAction({
            deviceType: 'currentAudioOutputDevice',
            deviceValue: audioOutputDevices?.[0]?.value,
          })
        );
      }
      if (videoInputDevices.length > 0) {
        yield audioVideo.chooseVideoInputDevice(null);
        yield put(
          chimeActions.setCurrentDeviceAction({
            deviceType: 'currentVideoInputDevice',
            deviceValue: videoInputDevices?.[0]?.value,
          })
        );
      }
    }
    yield put(chimeActions.successAction({ key: 'getAudioVideoDevices' }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key: 'getAudioVideoDevices', error: error.message }));
  }
}

export function* addAudioObserversSaga(element) {
  yield put(chimeActions.initAction({ key: 'addAudioObservers' }));
  try {
    const { audioVideo } = yield select(({ chime }) => ({
      audioVideo: chime.audioVideo,
    }));

    audioVideo.bindAudioElement(element);

    yield put(chimeActions.successAction({ key: 'addAudioObservers' }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key: 'addAudioObservers', error: error.message }));
  }
}
